/** @jsx jsx */
// eslint-disable-next-line
import React from 'react'
import { Button, CircularProgress, MenuItem, TextField, withStyles } from '@material-ui/core'
import { css, jsx } from '@emotion/core'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { theme } from '../../../constants'
import formatDate from '../../../utils/formatDate'
import OrganizationService from '../../../services/organization'
import Multiselect from '../../components/Multiselect'
import * as Actions from '../../admin/actions'
import { getPlanName } from '../../../utils/planName'

const styles = {}

styles.blockingOverlay = css`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 999;
  background: rgba(0, 0, 0, 0.5);
`

styles.box = css`
  position: absolute;
  width: 750px;
  border-radius: 5px;
  padding: 30px 60px;
  background: white;
  border: 10px solid lightgray;
  pointer-events: all;
`

styles.title = css`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 20px;
  font-weight: bold;
  color: ${theme.palette.common.black};
`

styles.error = css`
  color: ${theme.palette.error.main};
  margin: 0px 30px 15px 30px;
`

styles.descriptionContainer = css`
  display: flex;
  width: 100%;
  height: 40px;
  justify-content: space-between;
  margin: 15px 0px;
  color: ${theme.palette.common.black};
`

styles.label = css`
  width: 30%;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
`

styles.description = css`
  width: 70%;
  display: flex;
  align-items: center;
  font-size: 14px;
`

styles.selectForm = css`
  width: 100%;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
  background: transparent;
`

styles.loaderContainer = css`
  margin-left: 10px;
`

styles.actionContainer = css`
  display: flex;
  align-items: center;
  justify-content: end;
  margin-top: 10px;
`

styles.actionButton = css`
  height: 24px;
  padding: 0px 20px;
  text-transform: none;
  font-size: 14px;
  background: ${theme.palette.common.blue};
  color: ${theme.palette.common.white};

  &:hover {
    background: ${theme.palette.common.blue2};
  }
`

styles.cancelButton = css`
  height: 24px;
  padding: 0px 20px;
  margin-right: 15px;
  text-transform: none;
  font-size: 14px;
  background: transparent;
  color: ${theme.palette.common.blue};
  box-shadow: none;

  &:hover {
    background: transparent;
  }
`

styles.loaderContainer = css`
  display: flex;
  color: white;
  margin-right: 10px;
`

styles.error = css`
  color: ${theme.palette.error.main};
  height: 20px;
  text-align: right;
  font-size: 14px;
`

styles.iconClose = css`
  position: absolute;
  display: flex;
  color: grey;
  top: 20px;
  right: 24px;
  align-items: flex-start;
  justify-content: flex-end;
  font-size: 28px;
  cursor: pointer;
`

const CssSelect = withStyles({
  root: {
    '& .MuiOutlinedInput-input': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
      backgroundColor: 'transparent',
      paddingBottom: '8px',
      paddingTop: '10px',
      fontSize: '14px'
    },

    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.text.disabled
      }
    }
  }
})(TextField)

const CssMultiselect = withStyles({
  root: {
    width: '100%',

    '& .MuiFormLabel-root': {
      color: theme.palette.common.black,
      fontSize: '19px',
      fontWeight: 'bold'
    },
    '& .MuiInput-formControl': {
      marginTop: '24px'
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: `${theme.palette.text.disabled}`
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.text.disabled
      }
    },
    '& .MuiChip-root': {
      color: theme.palette.common.white,
      background: theme.palette.common.blue
    },
    '& .MuiChip-deleteIcon': {
      color: 'rgba(255, 255, 255, 0.75)',

      '&:hover': {
        color: theme.palette.common.white
      }
    }
  }
})(Multiselect)

const CssMenuItem = withStyles({
  root: {
    fontSize: '14px'
  }
})(MenuItem)

const CssButton = withStyles({
  root: {
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.5)'
    }
  }
})(Button)

function EditOrganizationUserModal(props) {
  const [userKey, setUserKey] = React.useState(props.organizationUser.user_key)
  const [role, setRole] = React.useState(props.organizationUser.role)
  const [organizations, setOrganizations] = React.useState([])
  const [allOrganizations, setAllOrganizations] = React.useState([])
  const [suggestions, setSuggestions] = React.useState([])
  const [isEnabled, setIsEnabled] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [showError, setShowError] = React.useState('')

  React.useEffect(() => {
    const allOrgs = props.groupOrganizations.map(v => ({ value: v._id, label: v.name }))
    setAllOrganizations(allOrgs)

    const userOrgs = props.organizationUser.organizations.map(v => ({
      value: v.organization._id,
      label: v.organization.name
    }))
    setOrganizations(userOrgs)

    setSuggestions(allOrgs.filter(v => !userOrgs.some(w => v.value === w.value)))
  }, [props.groupOrganizations])

  // Allow saving only if something has changed.
  function updateIsEnabled(userKeyCode, roleId, userOrganizations) {
    const userKeyChanged =
      (userKeyCode && !props.organizationUser.user_key) ||
      (!userKeyCode && props.organizationUser.user_key) ||
      (props.organizationUser.user_key && userKeyCode !== props.organizationUser.user_key.code)
    const roleChanged = roleId !== props.organizationUser.role
    const userOrganizationsChanged =
      userOrganizations.length !== props.organizationUser.organizations.length ||
      userOrganizations.some(
        v => !props.organizationUser.organizations.some(w => v.value === w.organization._id)
      ) ||
      props.organizationUser.organizations.some(
        v => !userOrganizations.some(w => v.organization._id === w.value)
      )

    setIsEnabled(userKeyChanged || roleChanged || userOrganizationsChanged)
  }

  function onPlanChange(event) {
    const userKeyCode = event.target.value
    const subscriptionUserKey = props.subscriptions.reduce((acc, v) => {
      if (acc) {
        return acc
      }

      if (v.order) {
        for (let i = 0; i < v.order.items.length; i += 1) {
          const iUserKey = v.order.items[i].user_key
          if (iUserKey && iUserKey.code === userKeyCode) {
            return iUserKey
          }
        }
      }

      if (v.userKey && v.userKey.code === userKeyCode) {
        return v.userKey
      }

      return null
    }, null)
    setUserKey(subscriptionUserKey)

    updateIsEnabled(userKeyCode, role, organizations)
  }

  function onRoleChange(event) {
    const roleId = event.target.value
    setRole(roleId)

    updateIsEnabled(userKey ? userKey.code : null, roleId, organizations)
  }

  function onChangeOrganizations(userOrganizations) {
    setOrganizations(userOrganizations)
    updateIsEnabled(userKey ? userKey.code : null, role, userOrganizations)

    const suggestionOrganizations = allOrganizations.filter(
      v => !userOrganizations.some(w => v.value === w.value)
    )
    setSuggestions(suggestionOrganizations)
  }

  async function onSubmit() {
    // On submit, show loader and disable submit to not perform many retries with the same key.
    setIsLoading(true)
    setIsEnabled(false)

    try {
      const payload = {
        email: props.organizationUser.email,
        role,
        userKey: userKey ? userKey._id : null,
        organizations: organizations.map(v => v.value)
      }
      await OrganizationService.updateUser(props.ownerOrganization._id, payload)

      // Reload UI.
      await Actions.updateUser()
      onClose()
    } catch (error) {
      const message = error.response && error.response.data && error.response.data.message
      if (message) {
        setShowError(message)
        if (!showError) {
          setTimeout(() => setShowError(''), 5000)
        }

        setIsLoading(false)
      } else {
        console.error(error)
        toast.error(window.I18n.t('users.updateErrorMessage'))
        onClose()
      }
    }
  }

  function onClose() {
    props.onApply()
  }

  return (
    <div css={styles.blockingOverlay}>
      <div css={styles.box}>
        <span css={styles.title}>{window.I18n.t('users.editUser')}</span>

        <div css={styles.descriptionContainer}>
          <span css={styles.label}>{window.I18n.t('common.email')}</span>
          <span css={styles.description}>{props.organizationUser.email}</span>
        </div>
        <div css={styles.descriptionContainer}>
          <span css={styles.label}>{window.I18n.t('users.plan')}</span>
          <span css={styles.description}>
            <CssSelect
              select
              variant="outlined"
              labelId="plan"
              id="plan"
              onChange={onPlanChange}
              value={userKey ? userKey.code : 'unassigned'}
              css={styles.selectForm}
            >
              {props.subscriptions
                .reduce((acc, v) => {
                  if (v.order) {
                    for (let i = 0; i < v.order.items.length; i += 1) {
                      const iUserKey = v.order.items[i].user_key
                      const iUserKeyBaseTemplate =
                        iUserKey &&
                        iUserKey.licence_configuration &&
                        iUserKey.licence_configuration.base_template
                      if (
                        iUserKey &&
                        ((props.organizationUser.user_key &&
                          iUserKey.code === props.organizationUser.user_key.code) ||
                          (iUserKeyBaseTemplate && iUserKey.max_usage > iUserKey.count_usage))
                      ) {
                        acc.push({ ...v, userKey: iUserKey, planName: getPlanName(iUserKey) })
                      }
                    }
                  } else if (
                    v.userKey &&
                    props.organizationUser.user_key &&
                    v.userKey.code === props.organizationUser.user_key.code
                  ) {
                    acc.push(v)
                  }

                  return acc
                }, [])
                .map(v => {
                  const isCurrent =
                    v.userKey && props.organizationUser.user_key
                      ? v.userKey.code === props.organizationUser.user_key.code
                      : false
                  // Count pending users with the user-key assigned to them.
                  let availableCount = v.userKey.max_usage - v.userKey.count_usage
                  for (const organizationUser of props.ownerOrganization.users) {
                    if (
                      !organizationUser.user &&
                      organizationUser.user_key &&
                      organizationUser.user_key.code === v.userKey.code &&
                      availableCount > 0
                    ) {
                      availableCount -= 1
                    }
                  }

                  const renewalAt =
                    v.order && (v.order.renewal_at || v.userKey.licence_expiration_date)
                  const renewal = renewalAt
                    ? window.I18n.t('users.renewsOn', { date: formatDate(renewalAt) })
                    : ''

                  return (
                    <CssMenuItem value={v.userKey.code}>
                      {isCurrent
                        ? window.I18n.t('users.currentLicense', { planName: v.planName, renewal })
                        : window.I18n.t('users.availableLicenses', {
                            planName: v.planName,
                            count: availableCount,
                            renewal
                          })}
                    </CssMenuItem>
                  )
                })}
              <CssMenuItem value={'unassigned'}>{window.I18n.t('common.unassigned')}</CssMenuItem>
            </CssSelect>
          </span>
        </div>

        <div css={styles.descriptionContainer}>
          <span css={styles.label}>{window.I18n.t('users.role')}</span>
          <span css={styles.description}>
            <CssSelect
              select
              variant="outlined"
              disabled={props.organizationUser.role === 'owner'}
              labelId="role"
              id="role"
              onChange={onRoleChange}
              value={role}
              css={styles.selectForm}
            >
              {['owner', 'admin', 'member']
                .filter(v => {
                  return v !== 'owner' || props.organizationUser.role === 'owner'
                })
                .map(v => {
                  return (
                    <CssMenuItem value={v} key={v}>
                      {window.I18n.t(`users.roles.${v}`)}
                    </CssMenuItem>
                  )
                })}
            </CssSelect>
          </span>
        </div>

        <div>
          <CssMultiselect
            name="organizations"
            label={window.I18n.t('users.userGroups')}
            placeholder={window.I18n.t('users.enterGroup')}
            onChange={onChangeOrganizations}
            fetchSuggestions={false}
            suggestions={suggestions}
            initialSelected={organizations}
          />
        </div>

        <div css={styles.error}>{showError}</div>
        <div css={styles.actionContainer}>
          <CssButton
            color="primary"
            variant="contained"
            css={styles.cancelButton}
            disabled={isLoading}
            onClick={onClose}
          >
            {window.I18n.t('common.cancel')}
          </CssButton>
          <CssButton disabled={!isEnabled} onClick={onSubmit} css={styles.actionButton}>
            {isLoading && (
              <div css={styles.loaderContainer}>
                <CircularProgress size={16} thickness={4} color="inherit" />
              </div>
            )}
            {window.I18n.t('common.save')}
          </CssButton>
        </div>
        <span
          css={styles.iconClose}
          style={isLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}}
          onClick={onClose}
        >
          {' '}
          x{' '}
        </span>
      </div>
    </div>
  )
}

EditOrganizationUserModal.defaultProps = {}

EditOrganizationUserModal.propTypes = {
  organizationUser: PropTypes.object.isRequired,
  groupOrganizations: PropTypes.array.isRequired,
  ownerOrganization: PropTypes.object.isRequired,
  subscriptions: PropTypes.array.isRequired,
  onApply: PropTypes.func.isRequired
}

export default EditOrganizationUserModal
